/**
 * JSON-LD 结构化数据。
 *
 * 只做仍然能拿到富摘要的三种：
 *   Organization      —— 品牌实体，有助于知识面板
 *   SoftwareApplication —— 本品类最相关，可展示价格与评分
 *   BreadcrumbList    —— 搜索结果里显示面包屑路径
 *
 * 刻意没做 FAQPage 和 HowTo：Google 在 2023 年把 FAQPage 富摘要限制到了
 * 政府与医疗类站点，HowTo 富摘要则已下线。给本站加这两种只会增加页面体积，
 * 换不回展示效果。
 *
 * 价格取自各语言 lang.js 的 index.price.plans（静态数据），
 * 改价时这里会跟着变，不需要单独维护。
 */
import { SITE_ORIGIN, DEFAULT_OG_IMAGE, canonicalUrl, findPage, stripLocale } from './seo'

const ORG_ID = `${SITE_ORIGIN}/#organization`
const APP_ID = `${SITE_ORIGIN}/#software`

const ORG_NAME = { zh: '北京灵动无线网络科技有限公司', en: 'Beijing Lingdong Wireless Technology Co., Ltd.' }

function organization(locale) {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: ORG_NAME[locale],
    alternateName: 'Design Mirror',
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/images/logo-mirror.png`,
    sameAs: ['https://weibo.com/u/5065292041', 'https://twitter.com/UI_DesignMirror']
  }
}

/**
 * 从 i18n 的价格方案里取出真实售价区间。
 *
 * 团购档的 price 存的是折扣数而不是金额（中文 currency:"折" price:80，
 * 英文 period:"% OFF" price:20），必须排掉，否则 offerCount 会多算一档、
 * 价格区间也会被污染。判据是 period 或 currency 里带百分号/折。
 */
function offersFrom(plans, locale) {
  const isDiscount = p =>
    /%/.test(String(p.period || '')) || /折/.test(String(p.currency || ''))
  const priced = (plans || []).filter(
    p => typeof p.price === 'number' && p.period && !isDiscount(p)
  )
  if (!priced.length) return null
  const prices = priced.map(p => p.price)
  return {
    '@type': 'AggregateOffer',
    priceCurrency: locale === 'zh' ? 'CNY' : 'USD',
    lowPrice: Math.min(...prices),
    highPrice: Math.max(...prices),
    offerCount: priced.length
  }
}

function softwareApplication(locale, copy, plans) {
  const offers = offersFrom(plans, locale)
  return {
    '@type': 'SoftwareApplication',
    '@id': APP_ID,
    name: 'Design Mirror',
    applicationCategory: 'DesignApplication',
    operatingSystem: 'Windows, macOS, iOS, Android',
    url: SITE_ORIGIN,
    image: SITE_ORIGIN + DEFAULT_OG_IMAGE,
    description: copy.description,
    inLanguage: locale === 'zh' ? 'zh-CN' : 'en',
    publisher: { '@id': ORG_ID },
    ...(offers ? { offers } : {})
  }
}

function breadcrumb(path, locale, copy) {
  if (path === '/') return null
  const home = locale === 'zh' ? '首页' : 'Home'
  // 标题里的品牌后缀在面包屑中是噪音，去掉
  const name = copy.title.split(/\s+[-|]\s+/)[0]
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: home, item: canonicalUrl('/', locale) },
      { '@type': 'ListItem', position: 2, name, item: canonicalUrl(path, locale) }
    ]
  }
}

/**
 * 返回该路由要注入的 JSON-LD 图。不可索引的页面不注入。
 * plans 由调用方从 i18n 取，避免这里直接依赖 vue-i18n 实例。
 */
export function buildJsonLd(routePath, plans) {
  const { path, locale } = stripLocale(routePath)
  const page = findPage(path)
  if (!page || page.index === false) return null
  const copy = page[locale]
  if (!copy || !copy.title) return null

  const graph = [organization(locale)]
  if (path === '/') {
    graph.push(softwareApplication(locale, copy, plans))
  } else {
    const bc = breadcrumb(path, locale, copy)
    if (bc) graph.push(bc)
  }

  return { '@context': 'https://schema.org', '@graph': graph }
}
